/**
 * HrV1ReviewsIndex.tsx
 *
 * Cross-cycle list of 360 review status (Step 2 of the 7-step cycle).
 * Mirrors HrV1CalibrationIndex / HrV1AppraisalsIndex / HrV1AppealsIndex
 * — closes the navigation gap that previously left Step 2 with no
 * cross-cycle entry on the sidebar.
 *
 * Route: /hr-v1/reviews (HR-only)
 *
 * Per-row shows: cycle name + (submitted / issued) progress for the
 * 360 panel. Click a row → drill into the cycle workflow focused on
 * Step 2.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../config/firebase';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import { useToast } from '../components/ui/Toast';
import { listHrV1Cycles } from '../services/hrV1CycleService';
import { routes } from '../routes';
import type { ReviewCycle } from '../types';

interface Row {
  cycle: ReviewCycle;
  issued: number;       // 360 magic-link tokens minted for this cycle
  submitted: number;    // review360Submissions with submittedAt set
}

const HrV1ReviewsIndex: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cycles = await listHrV1Cycles();
      const out: Row[] = [];
      for (const c of cycles) {
        try {
          // Issued = magicLinkTokens with action 'review360_submit' for this cycle.
          // Submitted = review360Submissions with a non-null submittedAt.
          // Both via getCountFromServer — cheap, no doc reads needed.
          const [issuedSnap, submittedSnap] = await Promise.all([
            getCountFromServer(query(
              collection(db, 'magicLinkTokens'),
              where('cycleId',  '==', c.id),
              where('action',   '==', 'review360_submit'),
            )),
            getCountFromServer(query(
              collection(db, 'review360Submissions'),
              where('cycleId',     '==', c.id),
              where('submittedAt', '!=', null),
            )),
          ]);
          out.push({
            cycle:     c,
            issued:    issuedSnap.data().count,
            submitted: submittedSnap.data().count,
          });
        } catch {
          // Skip cycles we cannot read — don't block the whole index
          // on one rule denial. The user will see the other cycles.
        }
      }
      // Sort by descending pending count — cycles needing chasing first.
      out.sort((a, b) => (b.issued - b.submitted) - (a.issued - a.submitted));
      setRows(out);
    } catch (e) {
      toast.show(`Failed to load: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <CenterMessage tone="info" title="Loading 360 reviews..." />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header>
        <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Console · Step 2 of 7</div>
        <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">360 Review — Cross-cycle status</h1>
        <p className="text-sm text-stone-600 mt-1">
          Step 2 of the review cycle. Each row shows how many 360 reviewer panels
          have been submitted vs issued. Reviewers fill via a magic link — no login
          needed. Cycles with the largest pending gap appear first.
        </p>
      </header>

      {rows.length === 0 ? (
        <CenterMessage
          tone="info"
          title="No cycles yet"
          body="360 review opens after Step 1 (EPR submission) is launched. Start a new review cycle first."
          action={
            <Link to={routes.cyclesList()}>
              <Button variant="submit" icon="add">Start a new review cycle</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-3">
          {rows.map(r => <ReviewRow key={r.cycle.id} row={r} />)}
        </div>
      )}
    </div>
  );
};

const ReviewRow: React.FC<{ row: Row }> = ({ row }) => {
  const { cycle, issued, submitted } = row;
  const pending = Math.max(0, issued - submitted);

  const status: string =
    issued === 0      ? 'not started'
    : pending === 0   ? 'all submitted'
                      : `${submitted}/${issued} · ${pending} left`;

  const tone: 'success' | 'info' | 'warning' | 'neutral' =
    issued === 0      ? 'neutral'
    : pending === 0   ? 'success'
                      : 'info';

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-stone-900">{cycle.name}</h3>
            <DisclosureBadge icon="groups" label={status} tone={tone} />
          </div>
          <div className="text-xs text-stone-500 mt-1">
            Period {cycle.period} {cycle.year}
            {issued > 0 && ` · ${submitted} of ${issued} reviewer panel${issued !== 1 ? 's' : ''} submitted`}
          </div>
        </div>
        <Link to={routes.hrV1Cycle(cycle.id, { stepHash: 2 })}>
          <Button
            variant={pending > 0 ? 'submit' : 'secondary'}
            icon="open_in_new"
          >
            Open Step 2
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default HrV1ReviewsIndex;
